

import { NewFormatValue, FileValue, ExtraInfoValue } from "./types";

export const isNewFormat = (value: any): value is NewFormatValue =>
  typeof value === "object" &&
  value !== null &&
  ("text" in value ||
    "date" in value ||
    "file" in value ||
    "files" in value ||
    "boolean" in value);

export const isPhotoFormat = (value: any) =>
  typeof value === "object" && value !== null && "isPhoto" in value && "url" in value;

export const isPdfFormat = (value: any) =>
  typeof value === "object" && value !== null && "isPdf" in value && "url" in value;

export const isDateFormat = (value: any) =>
  typeof value === "object" && value !== null && "isDate" in value && "date" in value;

export const isSpecialType = (value: any, key: string) =>
  typeof value === "object" && value?.[key];

/** convert legacy/special values → files[] */
export const toFilesArray = (value: any): FileValue[] => {
  if (isNewFormat(value)) {
    if (Array.isArray(value.files)) return value.files.filter((f) => f?.url);
    if (value.file?.url) return [value.file];
    return [];
  }
  if (isPhotoFormat(value)) return [{ url: value.url, format: "image" }];
  if (isPdfFormat(value)) return [{ url: value.url, format: "pdf", fileName: value.fileName }];
  if (
    value &&
    typeof value === "object" &&
    value.fileName &&
    value.url &&
    !value.isPhoto &&
    !value.isDate
  ) {
    return [{ url: value.url, format: "pdf", fileName: value.fileName }];
  }
  return [];
};

/** normalize value → new format */
export function normalizeExtraInfoValue(value: any): NewFormatValue {
  if (typeof value === "string") return { text: value };

  if (value && typeof value === "object") {
    if (isNewFormat(value)) {
      const files = Array.isArray(value.files) ? value.files : value.file ? [value.file] : [];
      return { text: value.text, date: value.date, files: files.length ? files : undefined, boolean: value.boolean };
    }
    if (value.isPhoto && value.url) return { files: [{ url: value.url, format: "image" }] };
    if (value.isPdf && value.url) return { files: [{ url: value.url, format: "pdf", fileName: value.fileName }] };
    if (value.isDate && value.date) return { date: value.date };
    if (value.fileName && value.url) return { files: [{ url: value.url, format: "pdf", fileName: value.fileName }] };
  }

  return {};
}

export const formatNumberText = (value?: string | number) => {
  const raw = typeof value === "number" ? value.toString() : value?.toString();
  if (!raw) return value;

  const trimmed = raw.trim();
  const dotGrouped = trimmed.replace(/,/g, "");
  if (/^[+-]?\d{1,3}(?:\.\d{3})+$/.test(dotGrouped)) {
    const cleaned = dotGrouped.replace(/\./g, "");
    return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(dotGrouped)) return value;
  const hasSign = dotGrouped[0] === "-" || dotGrouped[0] === "+";
  const sign = hasSign ? dotGrouped[0] : "";
  const body = hasSign ? dotGrouped.slice(1) : dotGrouped;
  const [intPart, fracPart] = body.split(".");
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fracPart !== undefined ? `${sign}${formattedInt}.${fracPart}` : `${sign}${formattedInt}`;
};

/** filter empty/unused labels */
export function filterEmptyValues(labels: [string, any][]) {
  return labels
    .map(([name, value]) => {
      if (value === "" || value == null) return null;

      if (isNewFormat(value)) {
        const { text = "", date = "", file, files } = value;
        const hasText = !!text?.toString().trim();
        const hasDate = !!date?.toString().trim();
        const hasFile = !!file?.url || (Array.isArray(files) && files.some((f) => f?.url));
        return hasText || hasDate || hasFile ? [name, value] : null;
      }

      if (isSpecialType(value, "isCategory")) {
        const { isCategory, ...rest } = value;
        const filtered = Object.fromEntries(Object.entries(rest).filter(([_, v]) => v != null && v !== ""));
        return Object.keys(filtered).length ? [name, { ...filtered, isCategory: true }] : null;
      }

      return typeof value === "string" && value.trim() !== "" ? [name, value] : null;
    })
    .filter(Boolean) as [string, any][];
}

/** format extra info value for CSV export - combines text, date, and files into a single string */
export function formatExtraInfoValueForCsv(value: ExtraInfoValue): string {
  if (isSpecialType(value, 'isCategory')) {
    const categoryValues: string[] = [];
    Object.entries(value).forEach(([key, val]) => {
      if (key !== 'isCategory') {
        const { text, date, files } = normalizeExtraInfoValue(val);
        const formattedText = formatNumberText(text);
        const parts: string[] = [];
        if (formattedText !== undefined && formattedText !== null) parts.push(String(formattedText));
        if (date) parts.push(String(date));
        if (files?.length) {
          const fileType = files[0].format === 'pdf' ? '[PDF]' : '[Image]';
          parts.push(files.length > 1 ? `${fileType}(+${files.length - 1})` : fileType);
        }
        if (parts.length) categoryValues.push(parts.join(', '));
      }
    });
    return categoryValues.join('; ');
  } else {
    const { text, date, files } = normalizeExtraInfoValue(value);
    const formattedText = formatNumberText(text);
    const parts: string[] = [];
    if (formattedText !== undefined && formattedText !== null) parts.push(String(formattedText));
    if (date) parts.push(String(date));
    if (files?.length) {
      const fileType = files[0].format === 'pdf' ? '[PDF]' : '[Image]';
      parts.push(files.length > 1 ? `${fileType}(+${files.length - 1})` : fileType);
    }
    return parts.length ? parts.join(', ') : '-';
  }
}